import { useState, useEffect, useRef, Fragment } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Dropdown from 'react-bootstrap/Dropdown'

import { PscPhrase } from '../components/psc/psc-phrase'
import SaveButton from '../components/psc/lib/SaveButton'
import ErrorMessage from '../components/psc/lib/ErrorMessage'
import CancelButton from '../components/psc/lib/CancelButton'
import MultiSelect from '../components/psc/lib/MultiSelect'
import Confirm from '../components/psc/lib/Confirm'
import Checkbox from '../components/psc/lib/Checkbox'

import useAuthentication from '../hooks/useAuthentication';
import useProjectTypes from '../hooks/useProjectTypes'
import useProjectAccessLevels from '../hooks/useProjectAccessLevels'
import useQualityContents from '../hooks/useQualityContents'
import useRoleManagement from '../hooks/useRoleManagement'
import useUserManagement from '../hooks/useUserManagement'
import useUsergroupManagement from '../hooks/useUsergroupManagement'
import useBrowserTabTitle from '../hooks/useBrowserTabTitle'

const User = () => {
    const saveButtonRef = useRef();
    const navigate = useNavigate()
    const params = useParams();
    const isNew = params.userID === 'new';
    const userID = Number(params.userID);

    const { currentUser, updateUser } = useAuthentication();
    const { setBrowserTabTitle } = useBrowserTabTitle()

    const {
        userHasRole,
        activeUsers,
        updateUserAndSave,
        saveUserUpdates,
        reloadUsers,
    } = useUserManagement()

    const {
        usergroups,
        reloadUsergroups
    } = useUsergroupManagement()

    const {
        projectTypes,
    } = useProjectTypes()
    
    const {
        projectAccessLevels,
    } = useProjectAccessLevels()
    
    const {
        reloadQualityContents,
    } = useQualityContents()
    
    const {
        allRoles,
        roleGroups,
    } = useRoleManagement()

    // State management
    const [id, setID] = useState(0)
    const [forename, setForename] = useState('')
    const [surname, setSurname] = useState('')
    const [username, setUsername] = useState('')
    const [roles, setRoles] = useState([])
    const [projects, setProjects] = useState([])
    const [selectedUsergroups, setSelectedUsergroups] = useState([])

    const [error, setError] = useState(null)
    const [submitActive, setSubmitActive] = useState(false)
    const [allProjects, setAllProjects] = useState([])
    const [userProjectAccess, setUserProjectAccess] = useState([])
    
    useEffect(() => {
        fetch('/v0/projects?is_archived=0', { credentials: 'same-origin' })
            .then(function(response) {
                return response.json()
            })
            .then(function(json) {
                let projects = json.results.map(function (item) {
                    let res = {
                        id: item.id,
                        project_id: item.id,
                        project_type_id: item.project_type_id,
                        value: item.id,
                        label: item.project_code + ' - ' + item.name,
                        user_project_access_level_id: 1 // default when added
                    }
                    return res
                })
                setAllProjects(projects)
            })

        if (isNew) {
            return
        }

        fetch('/v0/users/'+userID+'/explicit_roles', { credentials: 'same-origin' })
            .then(function(response) {
                return response.json()
            })
            .then(function(json) {
                let data = json.result
                setID(data.id)
                setForename(data.forename)
                setSurname(data.surname)
                setUsername(data.username)
                setRoles(data.roles)
                setUserProjectAccess(data.user_project_access)
                setSelectedUsergroups(data.usergroups)
            })
        
    }, [isNew, userID])

    useEffect(() => {
        if( isNew ) {
            setBrowserTabTitle('New User')
            return
        }
        const name = `${forename} ${surname}`.trim()
        if( name ) {
            setBrowserTabTitle(name)
        }
    }, [isNew, forename, surname, setBrowserTabTitle])

    useEffect(() => {
        if( isNew ) {
            const role = allRoles.find(item => item.code === 'normal_user')
            setRoles(role ? [role] : [])
        }
    }, [allRoles, isNew])
    
    useEffect(() => {
        // Translate project ids ( in projectAccess ) to list of projects
        let list = []
        userProjectAccess.forEach(function (item) {
            let project = allProjects.find(function (p) {
                return item.project_id === p.id
            })
            if( !project ) {
                return
            }
            list.push({
                id: project.id,
                value: project.id,
                label: project.label,
                user_project_access_level_id: item.user_project_access_level_id,
                project_id: project.id,
                project_type_id: project.project_type_id,
                user_id: id
            })
        })

        list.sort(function (a,b) {
            if (a.label < b.label) {
                return -1;
            }
            if (a.label > b.label) {
                return 1;
            }
            // names must be equal
            return 0;
        })

        setProjects(list)
    }, [userProjectAccess, allProjects, id])

    const toBackend = () => {
        let data = {
            forename: forename,
            surname: surname,
            username: username,
            roles: roles,
            usergroups: selectedUsergroups,
            user_project_access: projects.map(function (item) {
                let res = {
                    project_id: item.project_id,
                    user_project_access_level_id: item.user_project_access_level_id
                }
                if( !isNew ) {
                    res.user_id = userID
                }
                return res
            })
        }
        if( !isNew ) {
            data.id = id
        }
        return data
    }
    
    const validateBeforeSubmit = () => {
        const errors = []
        if (forename === '') {
            errors.push('Forename is required')
        }
        if (surname === '') {
            errors.push('Surname is required')
        }
        if (username === '') {
            errors.push('Email is required')
        }
        if ( errors.length > 0 ) {
            setError(errors)
            return false
        }
        return true
    }

    const handleSubmitError = (props) => {
        setSubmitActive(false)
        setError([ props.message ])
    }

    const handleConfirmSubmit = () => {
        const actionProps = handleSubmit()
        saveButtonRef.current.submit(actionProps)
    }
    
    const handleSubmit = () => {
        if( submitActive || !validateBeforeSubmit() ) {
            return null
        }

        setSubmitActive(true)

        const userData = toBackend()
        updateUserAndSave(userID, userData)

        return {
            objType: 'users',
            objID: userID,
            data: userData,
            successCallback: function(json) {
                if(currentUser.id === userID) {
                    updateUser(json.result)
//                    reloadProjectContents() // In case we've been added access, we need to reload all content
                    reloadQualityContents() // Yea, same.
                }
                reloadUsers();
                reloadUsergroups();
            }
        }
    }

    const addAllProjects = (project_type_id, level_id) => {
        const current_level = {}
        projects.forEach(i => {
            current_level[i.id] = i.user_project_access_level_id
        })

        const newProjects = allProjects
            .filter(item => item.project_type_id === project_type_id)
            .map(item => ({
                ...item,
                user_project_access_level_id: current_level[item.id] || level_id
            }))

        const otherProjects = projects
            .filter(item => item.project_type_id !== project_type_id)

        const combined = [...newProjects, ...otherProjects]
            .sort((a, b) => a.label.localeCompare(b.label))

        setProjects(combined)
    }

    const removeAllProjects = (project_type_id) => {
        const filtered = projects.filter(
            item => item.project_type_id !== project_type_id
        )
        setProjects(filtered)
    }

    const toggleRoleCheckbox = (item) => {
        // If exists, remove. Otherwise add
        const exists = roles.find(role => role.id === item.id) ? true : false
        if( exists ) {
            const updated = roles.filter(role => role.id !== item.id)
            setRoles(updated)
            return
        }
        const updated = [ ...roles, item ]
        setRoles(updated)
    }

    const createRoleCheckbox = (item) => {
        let disabled = false

        const checked = roles.find(role => role.id === item.id) ? true : false

        if ( currentUser.id === userID ) {
            if ( item.code === 'normal_user' || item.code === 'user_admin' ) {
                disabled = true
            }
        }

        if ( item.code === 'full_access_admin' ) {
            disabled = true
        }

        const full_access_admin =  allRoles.find(r => r.code === 'full_access_admin');
        // User role should be disabled if the user has role "full_access_admin"
        if ( item.code === 'normal_user' && roles.find(r => r.code === 'full_access_admin')) {
            disabled = true
        }

        return (
            <div className="form-check" key={item.id}>
                <Checkbox
                    id={item.id}
                    name={item.id}
                    onChange={() => toggleRoleCheckbox(item)}
                    isChecked={checked}
                    disabled={disabled}
                />
                <label htmlFor={item.id}>
                    {item.name}
                    <span className="help-block m-b-none">
                        {item.description}
                    </span>
                </label>
            </div>
        )
    }

    const handleChangeRoleGroup = (group, role) => {
        // clear all in the group
        let update = roles.filter(item => item.role_group_id !== group.id)
        
        // now if there is a role, check it
        if(role) {
            update.push(role)
        }
        setRoles(update)
    }

    const createRoleGroup = (group) => {
        if( ! allRoles ) { return }

        let hasAnyRole = false
        let members = allRoles.filter(function (role) {
            return role.role_group_id === group.id
        }).map(function (role) {
            let className = 'btn btn-sm btn-white'
            if( roles.find(r => r.code === role.code)) {
                className = 'btn btn-sm btn-primary'
                hasAnyRole = true
            }
            return (
                <button
                    className={className}
                    type="button"
                    key={role.id}
                    value={role.id}
                    onClick={() => handleChangeRoleGroup(group, role)}
                    data-tooltip-id="gnctooltip" data-tooltip-content={role.tooltip}
                >
                    {role.name}
                </button>
            )
        })

        let noneClassName = ( hasAnyRole ) ? 'btn btn-sm btn-white' : 'btn btn-sm btn-primary'
        let none = (
            <button
                className={noneClassName}
                type="button"
                key="none"
                value="none"
                onClick={() => handleChangeRoleGroup(group)}
                data-tooltip-id="gnctooltip" data-tooltip-content="Cannot access this area"
            >
                None
            </button>
        )

        return (
            <tr key={group.id}>
                <td className="col-md-4">
                    {group.name}
                </td>
                <td className="col-md-4">
                    <div className="btn-group">
                        {none}
                        {members}
                    </div>
                </td>
            </tr>
        )
    }

    const renderUsergroups = () => {
        const name = `${forename} ${surname}`.trim() || 'The user'
        return (
            <>
            <div className="form-group" key="users-1">
                <label className="col-sm-2"></label>
                <div className="col-sm-8">
                    <h3 className="usergroups-header">Usergroups</h3>
                    <p>{name} belongs to the following groups.</p>
                </div>
            </div>
            <div className="form-group" key="usergroups-select">
                <label className="col-sm-2 control-label"></label>
                <div className="col-sm-8">
                    <MultiSelect
                        options={usergroups}
                        value={selectedUsergroups}
                        onChange={handleChangeUsergroups}
                        clearable={false}
                    />
                </div>
            </div>
            <div className="form-group">
                <label className="col-sm-2 control-label"></label>

                <div className="col-sm-8">
                    <table className="table">
                        <tbody>
                            {selectedUsergroups.map(function(item) {
                                 return (
                                     <tr key={item.id}>
                                         <td className="col-md-4">
                                             {item.label}
                                         </td>
                                         <td className="col-md-4">
                                         </td>
                                     </tr>
                                 )
                             })
                            }
                        </tbody>
                    </table>
                </div>
            </div>
        </>
        )
    }
    
    const handleChangeUsergroups = (newUsergroups) => {
        setSelectedUsergroups(newUsergroups)
    }
    
    const renderRoles = () => {
        let notInGroup = function(role) {
            return role.role_group_id === null
        }
        let rolesWithCheckboxes = allRoles.filter(notInGroup).map(createRoleCheckbox)
        let roleGroupsMap = roleGroups && roleGroups.map(createRoleGroup)

        let rolesElements = []

        let name = forename + ' ' + surname
        if ( name === ' ' ) {
            name = 'The user'
        }

        rolesElements.push(
            <div className="form-group" key="roles-1">
                <label className="col-sm-2"></label>
                <div className="col-sm-8">
                    <h3 className="roles-header">Roles</h3>
                    <p>{name} has the following system and administration roles.</p>

                    <span className="help-block m-b-none">
                        <small style={{fontWeight: 'normal'}}> {PscPhrase('ADMIN_USER_NOTE_ROLES')}</small>
                    </span>

                </div>

            </div>
        )

        rolesElements.push(
            <div className="form-group" key="plain-roles">
                <label className="col-sm-2"></label>
                <div className="col-sm-8">
                    {rolesWithCheckboxes}
                </div>
            </div>
        )

        rolesElements.push(
            <div className="form-group" key="empty-row-1">
                <label className="col-sm-2"></label>
                <div className="col-sm-8">
                </div>
            </div>
        )

        rolesElements.push(
            <div className="form-group" key="quality-1">
                <label className="col-sm-2"></label>
                <div className="col-sm-8">
                    <h3 className="quality-header">Quality Access</h3>
                    <p>{name} has the following quality information access.</p>
                </div>
            </div>
        )

        rolesElements.push(
            <div className="form-group" key="group-roles">
                <label className="col-sm-2"></label>
                <div className="col-sm-8">
                    <table className="table">
                        <tbody>
                            {roleGroupsMap}
                        </tbody>
                    </table>
                </div>
            </div>
        )

        rolesElements.push(
            <div className="form-group" key="empty-row-2">
                <label className="col-sm-2"></label>
                <div className="col-sm-8">
                </div>
            </div>
        )

        return rolesElements
    }

    const handleChangeProjectAccess = (projectTypeID, newProjects) => {
        const sortedProjects = [...newProjects].sort((a, b) => 
            a.label.localeCompare(b.label)
        )

        // Keep all projects with other project types
        const otherProjects = projects.filter(item => 
            item.project_type_id !== projectTypeID
        )

        setProjects([...sortedProjects, ...otherProjects])
    }

    const handleChangeProjectAccessLevel = (project, level_id) => {
        const updatedProjects = projects.map(item => ({
            ...item,
            user_project_access_level_id: item.id === project.id ? level_id : item.user_project_access_level_id
        }))
        setProjects(updatedProjects)
    }

    const renderAllProjectAccess = () => {
        if ( ! allProjects || ! projects || ! projectTypes ) {
            return (<div />)
        }
        return projectTypes.map(renderProjectAccess);
    }

    const renderProjectAccess = (projectType) => {
        let correctProjectType = function(item) {
            return item.project_type_id === projectType.id
        }
        let filteredAllProjects = allProjects.filter(correctProjectType)
        let filteredProjects = projects.filter(correctProjectType)

        let name = forename + ' ' + surname
        if ( name === ' ' ) {
            name = 'The user'
        }

        return (
            <Fragment key={`${projectType.id}-${projectType.name}`}>
            <div className="form-group" style={{marginBottom: 0}}>
                <label className="col-sm-2"></label>
                <div className="col-sm-8">
                    <h3 className={`${projectType.name.toLowerCase()}-header`}>{projectType.name} Access</h3>
                    <p>
                        {name} has the following access.
                    </p>
                    <div className="btn-group" style={{float: 'right'}}>
                        <Dropdown>
                            <Dropdown.Toggle size="xs" variant="white">
                                Add All As <b className="caret"></b>
                            </Dropdown.Toggle>

                            <Dropdown.Menu>
                                {projectAccessLevels.map((i) => {
                                     return (
                                         <Dropdown.Item key={i.code}>
                                             <button className="dropdown-item" onClick={() => addAllProjects(projectType.id, i.id)}>
                                                 {i.name}
                                             </button>
                                         </Dropdown.Item>
                                     )
                                 })
                                }
                            </Dropdown.Menu>
                        </Dropdown>
                        <button type="button" className="btn btn-white btn-xs"
                                onClick={() => removeAllProjects(projectType.id)}>
                            Remove All
                        </button>
                    </div>
                </div>
            </div>
            <div className="form-group">
                <label className="col-sm-2 control-label"></label>
                <div className="col-sm-8">
                    <MultiSelect
                        options={filteredAllProjects}
                        value={filteredProjects}
                        onChange={(value) => handleChangeProjectAccess(projectType.id, value)}
                        clearable={false}
                    />
                </div>
            </div>
            <div className="form-group">
                <label className="col-sm-2 control-label"></label>

                <div className="col-sm-8">
                    <table className="table">
                        <tbody>
                            {filteredProjects.map(function(item) {
                                 return (
                                     <tr key={item.id}>
                                         <td className="col-md-4">
                                             {item.label}
                                         </td>
                                         <td className="col-md-4">
                                             <div className="btn-group">
                                                 {projectAccessLevels && projectAccessLevels.map(function (i) {
                                                      let className = ( item.user_project_access_level_id === i.id ) ? 'btn btn-sm btn-primary' : 'btn btn-sm btn-white'
                                                      return (
                                                          <button
                                                              className={className}
                                                              type="button"
                                                              key={i.id}
                                                              value={i.id}
                                                              onClick={() => handleChangeProjectAccessLevel(item, i.id)}
                                                              data-tooltip-id="gnctooltip" data-tooltip-content={i.tooltip}
                                                          >
                                                              {i.name}
                                                          </button>
                                                      )
                                                  })
                                                 }
                                             </div>
                                         </td>
                                     </tr>
                                 )
                             })
                            }
                        </tbody>
                    </table>
                </div>
            </div>
            </Fragment>
        )
    }

    if (!userHasRole(currentUser,'user_admin') && 
        (currentUser.id !== userID)
    ) {
        navigate('/main')
        return null
    }
    
    let saveButton = (
        <SaveButton
            buttonVariant='primary'
            visible={true}
            spinnerActive={submitActive}
            label='Save Changes'
            onClick={handleSubmit}
            onError={handleSubmitError}
        />
    )
    if( isNew && username !== '' && forename !== '' && surname !== '' ) {
        const msg = 'Are you sure? This will send a welcome email with login information to: '+username
        saveButton = (
            <Confirm
                title='New User'
                bodyContent={msg}
                confirmText='Confirm'
                onConfirm={handleConfirmSubmit}
                >
                <SaveButton
                    buttonVariant='primary'
                    visible={true}
                    spinnerActive={submitActive}
                    label='Save Changes'
                    onClick={() => null}
                    onError={handleSubmitError}
                    ref={saveButtonRef}
                />
            </Confirm>
        )
    }

    let requiredIndicator = (<label className="form-required-star">&nbsp;*</label>);

    return (
        <div className="wrapper-content">
            <div className="row">
                <div className="col-lg-12">
                    <div className="ibox float-e-margins">
                        <div className="ibox-title">
                            <h2>User: {forename || '<New User>'} {surname}</h2>

                        </div>
                        <div className="ibox-content">
                            <form className="form-horizontal" onSubmit={(event) => event.preventDefault()}>
                                <div className="form-group">
                                    <label className="col-sm-2 control-label">Forename
                                        {requiredIndicator}
                                    </label>
                                    <div className="col-sm-8"><input className="form-control" type="text" name="forename" value={forename} onChange={(e) => setForename(e.target.value)} required /></div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">Surname
                                        {requiredIndicator}
                                    </label>
                                    <div className="col-sm-8"><input className="form-control" type="text" name="surname" value={surname} onChange={(e) => setSurname(e.target.value)} required /></div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">Email
                                        {requiredIndicator}
                                    </label>
                                    <div className="col-sm-8"><input className="form-control" type="text" name="username" value={username} onChange={(e) => setUsername(e.target.value.trim())} disabled={!isNew} required /></div>
                                </div>

                                <div className="hr-line-dashed"></div>

                                {renderUsergroups()}
                                
                                <div className="hr-line-dashed"></div>
                                
                                {renderRoles()}

                                {renderAllProjectAccess()}

                                <div className="hr-line-dashed"></div>

                                <div className="form-group">
                                    <ErrorMessage
                                        error={error}
                                    />
                                    
                                    <div className="col-sm-10 col-sm-offset-2">
                                        <CancelButton />

                                        {saveButton}
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default User
